import { createContext, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, type CollisionDetection, type DropAnimation,
  type PointerSensorOptions, type DraggableSyntheticListeners, type DraggableAttributes, type KeyboardCoordinateGetter,
} from "@dnd-kit/core";
import { defaultAnimateLayoutChanges, sortableKeyboardCoordinates, useSortable, type AnimateLayoutChanges } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CONTROL_SELECTOR, placeAt } from "../lib/dragModel";

/* 🔴 W96 — THE BUILDER'S DRAG ON @dnd-kit, ONE CONTRACT ON BOTH SURFACES (docs/NEXT-WORK.md W96; his yes 2026-09-29 ~23:45).
 * This module is the gesture's machinery; `lib/dragModel.ts` is its arithmetic; `WorkoutBuilder.tsx` decides what a drop
 * MEANS. What replaced what, so nobody re-derives it:
 *   · the HTML5 drag (`draggable`, `dragstart`/`dragover`/`drop`, the slots, `ghostOf`, `setDragImage`) is GONE — a
 *     pointer drag the page owns never meets the engines' "the source moved out from under the pointer" refusal (W80)
 *     or a `dragend` that never fires (W88);
 *   · the card FOLLOWS THE POINTER as a `DragOverlay` (0.92 opacity, lifted 1.02, no tilt, no browser snapshot image);
 *   · ONE GAP — the carried item's own node, drawn as an empty dashed space of exactly its height — starts where it was
 *     and travels as neighbours slide; nothing that looks like the card stays behind;
 *   · the gap moves only when the pointer crosses a neighbour's MIDPOINT as drawn (`midpointCollision`), the neighbours
 *     slide by dnd-kit's layout animation (150 ms), and the drop or the return animates 180 ms;
 *   · EVERY no-position ending (his list, `NO_POSITION_ENDINGS`) cancels through dnd-kit's own cancel, so the card goes
 *     back to its first place and nothing changes.
 */

/** Read by a screen reader when a grip takes focus (dnd-kit's `screenReaderInstructions`). The ⋯ menu's Move items are
 *  the other keyboard route (W28(e)). */
export const DRAG_INSTRUCTIONS = {
  draggable: "To pick this up, press Space or Enter. Use the arrow keys to move it, Space or Enter to put it down, and Escape to put it back where it was.",
};

export const SLIDE_MS = 150;
export const DROP_MS = 180;

/** `prefers-reduced-motion: reduce` — positions only, no slide and no landing. */
export function useReducedMotion(): boolean {
  const q = typeof window === "undefined" || typeof window.matchMedia !== "function"
    ? null : window.matchMedia("(prefers-reduced-motion: reduce)");
  const [reduced, setReduced] = useState(q?.matches ?? false);
  useEffect(() => {
    if (q === null) return;
    const on = () => setReduced(q.matches);
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, [q]);
  return reduced;
}

/* 🔴 A MOUSE PICKS UP ON THE GRIP AFTER 4 px; A FINGER HOLDS 150 ms ANYWHERE ON THE CARD. The pointer sensor ignores
   touch pointers, so a finger is the touch sensor's alone — two sensors racing for one touch is how a drag starts twice. */
class MousePointerSensor extends PointerSensor {
  static activators = [{
    eventName: "onPointerDown" as const,
    handler: ({ nativeEvent: e }: { nativeEvent: PointerEvent }, { onActivation }: PointerSensorOptions) => {
      if (e.pointerType === "touch" || !e.isPrimary || e.button !== 0) return false;
      onActivation?.({ event: e });
      return true;
    },
  }];
}

/** W137 cut 2: the Week's day drag shares these sensors and hands its own KEYBOARD geometry (a grid of days, not one list). */
export function useBuilderSensors(coordinateGetter: KeyboardCoordinateGetter = sortableKeyboardCoordinates) {
  return useSensors(
    useSensor(MousePointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter }),
  );
}

/** Autoscroll at the scroller's top and bottom tenth, ≤ 12 px per 16 ms (≈ 750 px/s) at the very edge — aimable.
 *  🔴 ONLY THE TWO LISTS SCROLL: dnd-kit scrolls every scrollable ancestor by default, and MEASURED (the driver's
 *  cross-part arm, 1440×1300) it scrolled the builder's own window up off the screen while the list travelled. The third is
 *  the stacked phone layout's scroller: at 375 the lists grow to their content and the builder's BODY scrolls (MEASURED:
 *  `.builder-work` overflow visible, `.wb-body` overflow auto) — without it a phone-width drag could not reach a card
 *  below the fold. */
export const LIST_SCROLLERS = ".builder-work, .wb-arrange-inner, .wb-body.builder-window-body";
export const AUTOSCROLL = {
  threshold: { x: 0, y: 0.1 }, acceleration: 12, interval: 16,
  canScroll: (el: Element) => el.matches(LIST_SCROLLERS),
};

/* What each droppable is, so one collision function can serve both surfaces. */
export type DropData =
  | { kind: "part"; surface: "main" | "outline"; section: number }
  | { kind: "run"; surface: "main" | "outline"; key: string }
  | { kind: "partitem"; section: number }
  | { kind: "parts" };

/** Where the carried thing would land, handed from the collision to the page as the collision's `data`. */
export type Target = { container: string; section: number | null; index: number };

/** 🔴 THE MIDPOINT COLLISION. `ask()` returns what the page is carrying and where it sits now:
 *   · `surface` — which list the pointer must be over (library drags aim at the main column);
 *   · `scroller` — that list's scroll box: a pointer outside it is over no list, so the gap goes home;
 *   · `order(section)` — the keys in a part as drawn now (runs), or the part order (a part drag);
 *   · `self` — the carried item's id in that list.
 *  It measures LIVE rectangles — transforms included, so the midpoints are where the neighbours are DRAWN, which is the
 *  hysteresis — and it answers with ONE collision: the container, carrying `{ section, index }` as its data. */
export function midpointCollision(ask: () => {
  mode: "runs" | "parts";
  surface: "main" | "outline";
  scroller: HTMLElement | null;
  order: (section: number) => readonly string[];
  idOf: (key: string) => string;
  self: string;
} | null): CollisionDetection {
  return ({ droppableContainers, pointerCoordinates, collisionRect }) => {
    const q = ask();
    if (q === null || q.scroller === null) return [];
    // A keyboard drag has no pointer: the overlay's centre stands in for it.
    const p = pointerCoordinates ?? { x: collisionRect.left + collisionRect.width / 2, y: collisionRect.top + collisionRect.height / 2 };
    const sr = q.scroller.getBoundingClientRect();
    if (p.x < sr.left || p.x > sr.right || p.y < sr.top || p.y > sr.bottom) return [];
    const byId = new Map(droppableContainers.map((c) => [String(c.id), c]));
    const mid = (id: string): number | null => {
      const el = byId.get(id)?.node.current;
      if (el === null || el === undefined) return null;
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2;
    };
    if (q.mode === "parts") {
      const list = droppableContainers.find((c) => (c.data.current as DropData | undefined)?.kind === "parts");
      if (list === undefined) return [];
      const mids = q.order(0).filter((k) => q.idOf(k) !== q.self).map((k) => mid(q.idOf(k))).filter((m): m is number => m !== null);
      return [{ id: list.id, data: { target: { container: String(list.id), section: null, index: placeAt(mids, p.y) } satisfies Target } }];
    }
    // The part under the pointer, or the nearest one vertically (the space between two parts belongs to the nearer).
    const parts = droppableContainers.filter((c) => {
      const d = c.data.current as DropData | undefined;
      return d?.kind === "part" && d.surface === q.surface;
    });
    let best: { id: string; section: number; dist: number } | null = null;
    for (const c of parts) {
      const el = c.node.current;
      if (el === null) continue;
      const r = el.getBoundingClientRect();
      const dist = p.y < r.top ? r.top - p.y : p.y > r.bottom ? p.y - r.bottom : 0;
      const section = (c.data.current as { section: number }).section;
      if (best === null || dist < best.dist) best = { id: String(c.id), section, dist };
    }
    if (best === null) return [];
    const others = q.order(best.section).filter((k) => q.idOf(k) !== q.self).map((k) => mid(q.idOf(k)));
    /* A FOLDED part draws none of its rows, so there is nothing to aim between: the drop lands at its END — a coach who
       cannot see the rows has not chosen a place among them. */
    const index = others.some((m) => m === null) ? others.length : placeAt(others as number[], p.y);
    return [{ id: best.id, data: { target: { container: best.id, section: best.section, index } satisfies Target } }];
  };
}

/** The target a collision carried, or null. */
export function targetOf(collisions: readonly { data?: Record<string, unknown> }[] | null): Target | null {
  const t = collisions?.[0]?.data?.target as Target | undefined;
  return t ?? null;
}

/* 🔴 NEIGHBOURS SLIDE WHEN THE ARRANGEMENT CHANGES — dnd-kit's layout animation (FLIP) for every item, not only the one
   being sorted, because the page moves the GAP by changing the arrangement rather than by transforming a static list. */
export const alwaysAnimate: AnimateLayoutChanges = (args) => defaultAnimateLayoutChanges({ ...args, wasDragging: true });

/** The drop and the return: the overlay lands on the gap, the gap's node hidden until it does. */
export function dropAnimation(reduced: boolean): DropAnimation | null {
  if (reduced) return null;
  return {
    duration: DROP_MS,
    easing: "cubic-bezier(0.2, 0, 0, 1)",
    sideEffects: ({ active }) => {
      active.node.style.opacity = "0";
      return () => { active.node.style.opacity = ""; };
    },
  };
}

/* ── One run in the main column ───────────────────────────────────────────────────────────────────────────────────── */

type Activator = {
  ref: (el: HTMLElement | null) => void;
  listeners: DraggableSyntheticListeners;
  attributes: DraggableAttributes;
} | null;
/** The run's grip reads its activator from here: an even superset's grip sits on the seam inside the run, an odd one's
 *  on the middle card, a loose card's on the card — one activator per run wherever it is drawn. */
export const GripContext = createContext<Activator>(null);
export const useGrip = () => useContext(GripContext);

/** A run — a loose card, or a superset's cards and seams — as ONE sortable item. The mouse starts on the grip; a finger
 *  anywhere on the run (the touch sensor's `onTouchStart` sits on the run itself). While carried it is the GAP: an empty
 *  dashed space of its own height (`is-placeholder`), its contents kept for their height and hidden. */
/** 🔴 THE GAP IS EXACTLY THE CARRIED ITEM'S HEIGHT — measured by the page at pick-up (`getBoundingClientRect`, before the
 *  item turns into the gap) and pinned. Left to its content the gap drifted: MEASURED by the driver, an Arrange row's gap
 *  lost its 1 px rule when it became a part's last row (the "no line under the last row" rule), and a carried part's gap
 *  grew 13 px. ⚠️ Not dnd-kit's `active.rect.initial`: MEASURED, that read 2 % tall (299 for a 293 px card) — the overlay's
 *  1.02 lift. */
/** 🔴 A LIST'S ITEM IDS, THE SAME ARRAY UNTIL THE ORDER CHANGES. dnd-kit's sortable turns the slide OFF for an item whose
 *  list reads as changed while its index equals the one it last settled at — and a list rebuilt on every render always
 *  reads as changed. MEASURED (the driver, frame by frame): every neighbour carried `transition: transform 0ms` and jumped
 *  303 px in one frame; with the ids kept stable it slides over `SLIDE_MS`. */
export function useStableIds(ids: readonly string[]): string[] {
  const key = ids.join("\u0000");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids ARE the key
  return useMemo(() => [...ids], [key]);
}

export const GapHeight = createContext<number | null>(null);

/** 🔴 THE FRAME THE DROP IS WRITTEN IN, NO ITEM SLIDES (2026-09-30, his report after the W96 deploy: *"something strange when
 *  releasing … kinda moves from down to up where it should"*). A run's id is its PLACE (`run:<part>:<first row>`), so when
 *  the drop is written every run after the landing takes a neighbour's id — and dnd-kit, told to animate every index change
 *  (`alwaysAnimate`), slid each one from where that id had been drawn. MEASURED by `drive.mjs --settle`: a card still
 *  moving 203 px (Arrange 25 px) after the overlay had landed, on a list that already stood in its final order. So the
 *  page says, for the one render that writes, that nothing is to animate; the order on screen does not change there. */
export const LayoutFrozen = createContext(false);
export function gapStyle(isDragging: boolean, h: number | null): CSSProperties {
  return !isDragging || h === null || h <= 0 ? {} : { height: h, boxSizing: "border-box", overflow: "hidden" };
}

export function SortableRun({ id, surface, runKey, disabled, reduced, children, className, handle = "grip" }: {
  id: string; surface: "main" | "outline"; runKey: string; disabled: boolean; reduced: boolean;
  children: ReactNode; className?: string;
  /** `grip` (a card: the mouse starts on its grip) or `whole` (an outline row: anywhere on it — a click still scrolls). */
  handle?: "grip" | "whole";
}) {
  const frozen = useContext(LayoutFrozen);
  const { setNodeRef, setActivatorNodeRef, listeners, attributes, transform, transition, isDragging } = useSortable({
    id, disabled, data: { kind: "run", surface, key: runKey } satisfies DropData,
    animateLayoutChanges: reduced || frozen ? () => false : alwaysAnimate,
    transition: reduced ? null : { duration: SLIDE_MS, easing: "ease" },
  });
  const gapH = useContext(GapHeight);
  const only = (names: readonly string[]): DraggableSyntheticListeners =>
    listeners === undefined ? undefined
      : Object.fromEntries(names.flatMap((n) => (listeners[n] === undefined ? [] : [[n, listeners[n]]])));
  const grip: Activator = disabled ? null : { ref: setActivatorNodeRef, listeners: only(["onPointerDown", "onKeyDown"]), attributes };
  const whole = handle === "whole" && !disabled;
  return (
    <li ref={setNodeRef} data-run={runKey}
        className={[className ?? "builder-run", isDragging ? "is-placeholder" : ""].filter(Boolean).join(" ")}
        style={{ transform: CSS.Translate.toString(transform), transition: transition ?? undefined, ...gapStyle(isDragging, gapH) }}
        {...(whole ? { ...attributes, ...listeners } : {})}
        onTouchStart={disabled ? undefined : listeners?.onTouchStart as React.TouchEventHandler<HTMLLIElement> | undefined}>
      <GripContext.Provider value={grip}>{children}</GripContext.Provider>
    </li>
  );
}

/* ── The no-position endings ──────────────────────────────────────────────────────────────────────────────────────── */

/** dnd-kit's own cancel: its pointer, touch and keyboard sensors all end a drag on an Escape `keydown` at the document —
 *  the same path a coach's Escape takes, so every ending below lands in `onDragCancel` and restores the first place. */
export function cancelDrag() {
  document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true }));
}

/** 🔴 While a drag is in flight, every ending he named that has no position cancels it: the pointer leaving the window,
 *  the window losing focus or being hidden, a scroll the coach made (a wheel — a touch cannot scroll a grip, which is
 *  `touch-action: none`, and a scrollbar is a second pointer), a resize, a second pointer or touch. (Escape and a release
 *  outside the list are dnd-kit's and the page's; a release on a control is judged at the drop, `releasedOnControl`.)
 *  ⚠️ NOT a `scroll` event: MEASURED (the driver's cross-part arm), the lists' own autoscroll scrolls the page while the
 *  card travels, and a scroll listener read that as the coach's scroll and cancelled every drag that had to travel.
 *  ⚠️ AND ESCAPE IS KEPT FROM THE WINDOW: the builder is a native `<dialog>`, whose close request is Escape's default
 *  action — so an Escape that puts the card back would also have closed the builder (MEASURED, the driver's Escape arm:
 *  the page was gone after it). Prevented in the capture phase while a drag is in flight; dnd-kit still hears the key. */
export function useNoPositionEndings(active: boolean, onEnding: (why: string) => void) {
  const say = useRef(onEnding);
  say.current = onEnding;
  useEffect(() => {
    if (!active) return;
    const end = (why: string) => () => { say.current(why); cancelDrag(); };
    const leave = (e: PointerEvent) => { if (e.relatedTarget === null) end("the pointer leaving the window")(); };
    /* A held mouse dragged past the window's edge keeps reporting moves at coordinates outside it, and does not always
       send a `pointerout` first (MEASURED: the driver's leave arm, a move to x = width + 40, sent none) — so a move outside
       the window is the same ending. */
    const outside = (e: PointerEvent) => {
      if (e.clientX < 0 || e.clientY < 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        end("the pointer leaving the window")();
      }
    };
    const blur = end("the window losing focus");
    const hidden = () => { if (document.visibilityState === "hidden") end("the window losing focus")(); };
    const wheel = end("a scroll mid-drag");
    const escape = (e: KeyboardEvent) => { if (e.key === "Escape") e.preventDefault(); };
    const resize = end("a resize mid-drag");
    const second = end("a second pointer or touch");
    const secondTouch = (e: TouchEvent) => { if (e.touches.length > 1) second(); };
    document.addEventListener("pointerout", leave);
    document.addEventListener("pointermove", outside);
    window.addEventListener("blur", blur);
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("wheel", wheel, { capture: true, passive: true });
    window.addEventListener("keydown", escape, { capture: true });
    window.addEventListener("resize", resize);
    document.addEventListener("pointerdown", second, { capture: true });
    document.addEventListener("touchstart", secondTouch, { capture: true, passive: true });
    return () => {
      document.removeEventListener("pointerout", leave);
      document.removeEventListener("pointermove", outside);
      window.removeEventListener("blur", blur);
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("wheel", wheel, { capture: true });
      window.removeEventListener("keydown", escape, { capture: true });
      window.removeEventListener("resize", resize);
      document.removeEventListener("pointerdown", second, { capture: true });
      document.removeEventListener("touchstart", secondTouch, { capture: true });
    };
  }, [active]);
}

/** "A release on a control": the pointer let go over a button, field, link or menu item that is NOT inside a list item
 *  (a card is full of fields — a release over one of them is a release over the card). The overlay takes no pointer. */
export function releasedOnControl(x: number, y: number): boolean {
  const el = document.elementFromPoint(x, y);
  if (el === null) return false;
  const control = el.closest(CONTROL_SELECTOR);
  if (control === null) return false;
  return control.closest(".builder-run, .builder-outline-exercise, .builder-outline-pair, .builder-outline-section-head") === null;
}

/** A static copy of the carried thing for the overlay: its own rendered look, ids and ARIA dropped so the page holds one
 *  of each. */
export function snapshotOf(el: HTMLElement | null, without?: string): string {
  if (el === null) return "";
  const copy = el.cloneNode(true) as HTMLElement;
  // W99: a part is carried as its HEAD — its rows are left out of the copy that follows the pointer.
  if (without !== undefined) for (const n of Array.from(copy.querySelectorAll(without))) n.remove();
  for (const n of [copy, ...Array.from(copy.querySelectorAll<HTMLElement>("*"))]) {
    n.removeAttribute("id");
    n.removeAttribute("aria-describedby");
    n.removeAttribute("aria-labelledby");
    n.removeAttribute("tabindex");
    n.removeAttribute("style");
  }
  copy.classList.remove("is-placeholder");
  return copy.outerHTML;
}
